import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  faTrashCan,
  faClockRotateLeft,
} from "@fortawesome/pro-solid-svg-icons";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  type BatchAction,
  useArchiveFilter,
  useBatchSelection,
  withErrorReason,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useFlags from "@common/hooks/useFlags";
import { useApprovalLevelsList } from "../api/useApprovalLevelsList";
import { useApprovalLevelMutations } from "../api/useApprovalLevelMutations";
import { approvalLevelColumns } from "./columns";
import "./style.css";

type PendingAction = "archive" | "restore" | "purge" | "consolidate" | null;

export const List = () => {
  const navigate = useNavigate();
  const { releaseUnifiedApprovalWorkflow } = useFlags();
  const { tab, setTab, isArchivedFilter, tabsFor } = useArchiveFilter();
  const { rows, totalCount, isLoading, isFetching, refetch } =
    useApprovalLevelsList({ archived: isArchivedFilter });
  const opposite = useApprovalLevelsList({ archived: !isArchivedFilter });
  const { destroy, move, restore, purge, consolidateTechnical } =
    useApprovalLevelMutations();

  const { selectedIds, setSelectedIds, clear } = useBatchSelection([tab]);
  const [pending, setPending] = useState<PendingAction>(null);
  const [toast, setToast] = useState<{
    variant: "success" | "error";
    text: string;
  } | null>(null);

  if (isLoading) return <ScopeStackSpinner />;

  const runBatch = async (
    fn: (id: string) => Promise<unknown>,
    successText: string,
    failText: string
  ) => {
    const ids = [...selectedIds];
    const failures: BatchFailure[] = [];
    for (const id of ids) {
      try {
        await fn(id);
      } catch (err) {
        failures.push({ reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? {
            variant: "success",
            text: successText.replace("{count}", String(ids.length)),
          }
        : {
            variant: "error",
            text: withFailureReasons(
              failText
                .replace("{failures}", String(failures.length))
                .replace("{count}", String(ids.length)),
              failures
            ),
          }
    );
    clear();
    setPending(null);
    refetch();
    opposite.refetch();
  };

  const handleConfirm = async () => {
    if (pending === "archive") {
      await runBatch(
        destroy,
        "{count} approval level(s) archived.",
        "Failed to archive {failures} of {count} approval levels."
      );
    } else if (pending === "restore") {
      await runBatch(
        restore,
        "{count} approval level(s) restored.",
        "Failed to restore {failures} of {count} approval levels."
      );
    } else if (pending === "purge") {
      await runBatch(
        purge,
        "{count} approval level(s) permanently deleted.",
        "Failed to permanently delete {failures} of {count} approval levels."
      );
    } else if (pending === "consolidate") {
      try {
        await consolidateTechnical();
        setToast({
          variant: "success",
          text: "Technical approval levels consolidated.",
        });
      } catch (err) {
        setToast({
          variant: "error",
          text: withErrorReason(
            "Failed to consolidate technical approval levels.",
            err
          ),
        });
      }
      clear();
      setPending(null);
      refetch();
      opposite.refetch();
    }
  };

  const handleReorder = async (
    moves: Array<{ id: string | number; position: number }>
  ) => {
    try {
      for (const { id, position } of moves) {
        await move(String(id), position);
      }
      refetch();
    } catch (err) {
      setToast({
        variant: "error",
        text: withErrorReason(
          "Failed to reorder. The list has been refreshed.",
          err
        ),
      });
      refetch();
    }
  };

  const counts = isArchivedFilter
    ? { active: opposite.totalCount, archived: totalCount }
    : { active: totalCount, archived: opposite.totalCount };

  // Consolidation flattens the account's ACTIVE kind=technical levels, so count
  // them from whichever query holds the active set for the current tab.
  const activeRows = isArchivedFilter ? opposite.rows : rows;
  const technicalCount = activeRows.filter(
    (r) => r.kind === "technical"
  ).length;

  const activeActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Archive",
      onClick: () => setPending("archive"),
    },
  ];

  const archivedActions: BatchAction[] = [
    {
      icon: faClockRotateLeft,
      label: "Restore",
      onClick: () => setPending("restore"),
    },
    {
      icon: faTrashCan,
      label: "Permanently delete",
      onClick: () => setPending("purge"),
      variant: "danger",
    },
  ];

  const batchActions = isArchivedFilter ? archivedActions : activeActions;

  return (
    <>
      <AdminListCard
        title="Approval Levels"
        onNew={() => navigate("./new")}
        newButtonLabel="+ Add Approval Level"
        secondaryAction={
          releaseUnifiedApprovalWorkflow &&
          !isArchivedFilter &&
          technicalCount > 1
            ? {
                label: "Consolidate Technical Levels",
                onClick: () => setPending("consolidate"),
              }
            : undefined
        }
        batchActions={batchActions}
        selectedCount={selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => setTab(key as "active" | "archived")}
          tabs={tabsFor(counts)}
        />
        <AdminListTable
          columns={approvalLevelColumns}
          data={rows}
          isLoading={isFetching}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          draggable={!isArchivedFilter}
          onReorder={!isArchivedFilter ? handleReorder : undefined}
          onEditHref={(row) => `/admin/approval_levels/${row.id}/edit`}
          editLinkDisabled={isArchivedFilter}
          emptyMessage={
            isArchivedFilter
              ? "No archived approval levels."
              : "No approval levels defined yet."
          }
        />
      </AdminListCard>
      <ConfirmDeleteModal
        open={pending === "archive"}
        title={`Archive approval level? (${selectedIds.length})`}
        message={`Move ${selectedIds.length} approval level(s) to Archived?`}
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
      <ConfirmDeleteModal
        open={pending === "restore"}
        title={`Restore approval level? (${selectedIds.length})`}
        message={`Restore ${selectedIds.length} approval level(s) to Active?`}
        confirmLabel="Restore"
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
      <ConfirmDeleteModal
        open={pending === "purge"}
        title={`Permanently delete approval level? (${selectedIds.length})`}
        message={`Permanently delete ${selectedIds.length} approval level(s)? This cannot be undone.`}
        confirmLabel="Permanently Delete"
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
      <ConfirmDeleteModal
        open={pending === "consolidate"}
        title="Consolidate technical approval levels?"
        message="This will combine all 'Technical' approval levels into a single level. It cannot be undone."
        confirmLabel="Consolidate"
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
